import type React from "react";
import { TextField, FormControl, Autocomplete } from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import { toastEmitter } from "../../utils/toastEmitter";
import FormDialog from "../common/FormDialog";
import { useFormDialog } from "../../hooks/useFormDialog";
import { formatLicensePlateInput } from "../../utils/formatLicensePlate";

type TranslationKeys = keyof typeof translations.en;

interface CreateTruckFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (truckData: {
    licensePlate: string;
    type: string;
  }) => Promise<void>;
  error: string | null;
  defaultType?: "WINGBOX" | "LOSBAK" | "DUMP";
}

const TRUCK_TYPES = ["WINGBOX", "DUMP", "LOSBAK"];

const CreateTruckForm: React.FC<CreateTruckFormProps> = ({
  open,
  onClose,
  onSubmit,
  error,
  defaultType,
}) => {
  const { t } = useTranslation();

  const {
    loading,
    formData,
    validationErrors,
    handleChange,
    handleSubmit,
    handleClose,
    setFormData,
  } = useFormDialog({
    initialData: { licensePlate: "", type: defaultType || "" },
    onSubmit: async (data) => {
      await onSubmit(data);
      toastEmitter.emit(t("Truck created successfully" as TranslationKeys), "success");
    },
    onClose,
    validate: (data) => {
      const errors: string[] = [];
      if (!data.licensePlate.trim()) {
        errors.push("License plate is required");
      }
      if (!data.type) {
        errors.push("Type is required");
      }
      return errors;
    },
  });

  const handleLicensePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatLicensePlateInput(e.target.value);
    setFormData((prev) => ({ ...prev, licensePlate: formattedValue }));
  };

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title="Create New Truck"
      loading={loading}
      loadingMessage="Creating truck..."
      error={error}
      validationErrors={validationErrors}
      submitDisabled={!formData.licensePlate || !formData.type}
      submitText="Add Truck"
    >
      <TextField
        autoFocus
        margin="dense"
        label={t("License Plate" as TranslationKeys)}
        name="licensePlate"
        type="text"
        fullWidth
        value={formData.licensePlate}
        onChange={handleLicensePlateChange}
        required
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth>
        <Autocomplete
          value={TRUCK_TYPES.find((type) => type === formData.type) || null}
          onChange={(_, newValue) => {
            setFormData((prev) => ({ ...prev, type: newValue || "" }));
          }}
          options={TRUCK_TYPES}
          getOptionLabel={(option) => t(option as TranslationKeys)}
          renderInput={(params) => (
            <TextField {...params} label={t("Type" as TranslationKeys)} required />
          )}
        />
      </FormControl>
    </FormDialog>
  );
};

export default CreateTruckForm;
